import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import { Badge as BadgeStyles } from '@airbus/elements/elements'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { createUseStyles } from '../../lib/styling'
import { CommonProps } from '../helperTypes'

const useStyles = createUseStyles(BadgeStyles, 'badge')

// FIXME default ariaLabel value ? or getAriaLabel function prop with count parameter ?
type Variants = 'default' | 'info' | 'success' | 'warning' | 'error'

type Props = PropsWithChildren<{
  /** Specify the label for screen readers */
  'aria-label'?: string
  /**
   * The count to show
   * @default 0
   */
  count?: number
  /**
   * Max count to show (with +)
   * @default 99
   */
  max?: number
  /**
   * The variant of the chip.
   * @default default
   */
  variant?: Variants
}>

type DeprecatedProps = {
  color?: 'primary' | 'secondary' | 'error' | 'success' | 'warning' | 'info'
}

export type BadgeProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: BadgeProps): BadgeProps => {
  const { color, variant: variantProp, ...rest } = props
  let variant = variantProp
  if (color) {
    if (['info', 'success', 'warning', 'error'].indexOf(color) !== -1) variant = color as Variants
    variant = 'default'
  }
  return {
    variant,
    ...rest,
  }
}

const Badge = (props: BadgeProps) => {
  const {
    'aria-label': ariaLabel,
    children,
    className,
    count = 0,
    max = 99,
    style,
    variant,
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('Badge', { color: 'variant' }, props)
  const classes = useStyles()

  return (
    <div
      className={clsx(classes.root, className, {
        [classes._error]: variant === 'error',
        [classes._success]: variant === 'success',
        [classes._warning]: variant === 'warning',
        [classes._info]: variant === 'info',
      })}
      style={style}
    >
      {children}
      <div aria-label={ariaLabel} className={classes.count} role='status' {...rest}>
        {count > max ? `${max}+` : count}
      </div>
    </div>
  )
}

export default Badge
